import * as React from 'react';
import { uuid } from 'uuidv4';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading, PageActionHeading } from '../../../../components/page-components/_index';
import { EditAddressHoursControl } from '../../../../support/components/dealers/EditAddressHoursControl';
import { PageLevelError } from '../../../../components/form-elements/PageLevelError';
import { AppUtils } from '../../../../utils/app-utils';
import { WeekDays, WeekDaysMetadata } from '../../../../models/enums/week-days';
import { ToggleInput, TextArea } from '../../../../components/form-elements/_index';
import { TransportationSvc } from '../../../../transportation/service';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import { TimeZoneInput } from '../../../../components/form-elements/TimeZoneInput';
import { PageActions, TextInput } from '../../../../components/form-elements/_index';
import { CountryInput } from '../../../../components/form-elements/CountryInput';
import { DeliveryHourView } from '../../../../shared/models/ship-to-address-read';
import { DeliveryAddressUpdate, DeliveryHourUpdate } from '../../../../support/models/dealers/delivery-address-update';
import { ModelValidator } from '../../../../utils/model-validator';
import { AddressApproveStatus, ShipToAddressApprove, ApproveAddress } from '../../../../transportation/models/address/transportation-approve-address';
import { AddressEntryDisplay } from '../../../../support/components/dealers/AddressEntryDisplay';
import { AddressDeliveryType, AddressDeliveryTypeMetadata, AddressDeliveryTypeSelectList } from '../../../../models/transportation/enums/addressDeliveryType';
import { SelectListItem } from '../../../../models/utilities/selectlist-item';
import YesNoCancelDialog from '../../../../components/dialog-types/YesNoCancelDialog';
import { showSuggestedAddressConfirmation } from '../../../../components/dialog-types/SuggestedAddressConfirmation';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import '../../../../transportation/styles.scss';
import { getApplicationStore } from '../../../../store/configure-store';
import { Table } from '../../../../components/tables/Table';
import { PendingAddress, PendingAddressOrder } from '../../../../transportation/models/address/pending-address';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';

interface IProps {
    match?: any;
    location?: any;
}

interface IGroupedHours {
    id: string;
    items: DeliveryHourView[];
    days: number;
}

export const AddressApprovalScene = (props: IProps) => {

    const store = getApplicationStore();

    const [currentAddress, setAddress] = React.useState(new PendingAddress());
    const [pageErrorMsg, setPageErrorMsg] = React.useState('');
    const [groupedHours, setGroupedHours] = React.useState<IGroupedHours[]>([]);
    const [hoursByControl, setHoursByControl] = React.useState({});
    const [fixedRangeMode, setFixedRangeMode] = React.useState(false);
    const [displayHours, setDisplayHours] = React.useState<any>({});
    const [errorMsgs, setErrorMsgs] = React.useState({} as any);
    const [service] = React.useState(new TransportationSvc());
    const [newDeliveryType, setNewDeliveryType] = React.useState<AddressDeliveryType | undefined>(undefined);
    const [deliveryTypes, setDeliveryTypes] = React.useState<Array<SelectListItem<string>>>([]);

    React.useEffect(() => {
        load();
    }, [props.match, service]);

    const load = async () => {
        const addressId = props.match.params.id;
        const serverHoursGrouped: IGroupedHours[] = [];
        const response = await service.getPendingApprovalAddressDetails(addressId);
        if (response.status !== StatusCode.Success || !response.data) {
            setPageErrorMsg('Unable to load address.');
            return;
        }

        const data = response.data;

        data.deliveryHours.forEach((serverItem: DeliveryHourView) => {

            if (serverHoursGrouped.length === 0) {
                const tmpDHV = new DeliveryHourView();
                tmpDHV.startTime = serverItem.startTime;
                tmpDHV.endTime = serverItem.endTime;
                tmpDHV.uniqueID = uuid();
                tmpDHV.weekDays = serverItem.weekDays;

                serverItem.uniqueID = uuid();

                serverHoursGrouped.push({
                    id: uuid(),
                    items: [tmpDHV],
                    days: serverItem.weekDays
                });
                return;
            }

            let foundMatch = false;
            let outerElement;
            for (let j = 0; j < serverHoursGrouped.length; j++) {

                outerElement = serverHoursGrouped[j];
                if (Array.isArray(outerElement.items)) {

                    for (let i = 0; i < outerElement.items.length; i++) {
                        const innerElement = outerElement.items[i];
                        if (innerElement.startTime === serverItem.startTime &&
                            innerElement.endTime === serverItem.endTime) {
                            foundMatch = true;
                            break;
                        }
                    }
                }

                if (foundMatch) {
                    break;
                }
            }

            serverItem.uniqueID = uuid();
            if (foundMatch && outerElement) {
                outerElement.items.push(serverItem);
            }
            else {
                const tmpDHV = new DeliveryHourView();
                tmpDHV.startTime = serverItem.startTime;
                tmpDHV.endTime = serverItem.endTime;
                tmpDHV.uniqueID = uuid();
                tmpDHV.weekDays = serverItem.weekDays;

                serverHoursGrouped.push({
                    id: uuid(),
                    items: [tmpDHV],
                    days: serverItem.weekDays
                });
            }
        });

        if ((data.deliveryHours && data.deliveryHours.length > 0) &&
            (data.deliveryHours[0].startTime === data.deliveryHours[0].endTime)) {
            setFixedRangeMode(true);
        }

        setGroupedHours(serverHoursGrouped);
        setAddress(data);
        var deliveryTypes = getRemainingDeliveryTypes(data);
        setDeliveryTypes(deliveryTypes);
        setPageErrorMsg('');
    };

    function getRemainingDeliveryTypes(address: PendingAddress) {

        var remainingTypes = new Array<SelectListItem<string>>();
        for (let type of AddressDeliveryTypeSelectList) {
            if (!address.deliveryTypes.includes(Number(type.id))) {
                remainingTypes.push(type);
            }
        }
        return remainingTypes;
    }

    /**
     * Save the changes via the transportation service
     */
    const onSave = async () => {
        if (!currentAddress) {
            return;
        }

        const updateVM = new DeliveryAddressUpdate();
        updateVM.name = currentAddress.name;
        updateVM.addressLine1 = currentAddress.address.addressLine1;
        updateVM.addressLine2 = currentAddress.address.addressLine2 || null;
        updateVM.city = currentAddress.address.city;
        updateVM.stateProvince = currentAddress.address.stateProvince;
        updateVM.postalCode = currentAddress.address.postalCode;
        updateVM.latitude = currentAddress.address.latitude;
        updateVM.longitude = currentAddress.address.longitude;
        updateVM.timeZoneId = currentAddress.address.timeZoneId;
        updateVM.rowVersion = currentAddress.rowVersion;
        updateVM.unloadRate = currentAddress.unloadRate;
        updateVM.country = currentAddress.address.country;
        updateVM.phoneNumber = currentAddress.phoneNumber;
        updateVM.note = currentAddress.note;
        updateVM.isJobSite = currentAddress.isJobSite;
        updateVM.contactEmailBusiness = currentAddress.contactEmailBusiness;
        updateVM.contactNameBusiness = currentAddress.contactNameBusiness;
        updateVM.contactPhoneNumberBusiness = currentAddress.contactPhoneNumberBusiness;
        updateVM.contactEmailSite = currentAddress.contactEmailSite;
        updateVM.contactNameSite = currentAddress.contactNameSite;
        updateVM.contactPhoneNumberSite = currentAddress.contactPhoneNumberSite;
        updateVM.deliveryTypes = currentAddress.deliveryTypes;
        updateVM.isReviewed = currentAddress.isReviewed;

        currentAddress.deliveryHours.forEach((itm: DeliveryHourView) => {
            const hour = new DeliveryHourUpdate();
            hour.weekDays = itm.weekDays;
            hour.startTime = itm.startTime;
            hour.endTime = itm.endTime;

            updateVM.deliveryHours.push(hour);
        });

        setPageErrorMsg('');
        if (!validateAddressData(updateVM)) {
            return;
        }
        var addressId = currentAddress.id;
        let response = await service.saveAddressForApproval(currentAddress.id, updateVM);

        let data = response.data;
        if (data?.status === AddressApproveStatus.Success) {
            navigateToPage(Routes.APPROVALS_ADDRESSES);
            return;
        }

        if (data?.status === AddressApproveStatus.NotApprovedGoogleHasSuggestion) {
            const useGoogleAddress = await showSuggestedAddressConfirmation(updateVM, data.suggestedAddress);
            if (useGoogleAddress) {
                updateVM.addressLine1 = data.suggestedAddress.addressLine1;
                updateVM.addressLine2 = data.suggestedAddress.addressLine2;
                updateVM.city = data.suggestedAddress.city;
                updateVM.stateProvince = data.suggestedAddress.stateProvince;
                updateVM.postalCode = data.suggestedAddress.postalCode;
                updateVM.country = data.suggestedAddress.country || '';
                updateVM.latitude = data.suggestedAddress.latitude;
                updateVM.longitude = data.suggestedAddress.longitude;
                updateVM.timeZoneId = data.suggestedAddress.timeZoneId;
                updateVM.force = true;
            }
            else if (useGoogleAddress === false) {
                updateVM.force = true;
            }
            else {
                // User cancelled the confirmation
                return;
            }
        }
        else if (data?.status === AddressApproveStatus.NotApprovedGoogleNotFound) {
            const result = await showAddressConfirmation(updateVM);
            if (!result) {
                return;
            }
            updateVM.force = true;
        }
        else {
            setPageErrorMsg('Error occurred while updating the address. Please try again.');
            return;
        }

        response = await service.saveAddressForApproval(currentAddress.id, updateVM);
        if (response.status === StatusCode.Success && response.data.status === AddressApproveStatus.Success) {
            navigateToPage(Routes.APPROVALS_ADDRESSES);
            return;
        }
    };

    const onToggleReviewed = async (reviewed: boolean) => {
        var response = await service.toggleAddressReviewed(currentAddress.id, reviewed);
        if (response.status === StatusCode.Success) {
            load();
            return;
        }
    };

    const onApprove = async () => {
        if (!currentAddress) {
            return;
        }

        const approveVM = new ShipToAddressApprove();
        approveVM.name = currentAddress.name;
        approveVM.addressLine1 = currentAddress.address.addressLine1;
        approveVM.addressLine2 = currentAddress.address.addressLine2 || null;
        approveVM.city = currentAddress.address.city;
        approveVM.stateProvince = currentAddress.address.stateProvince;
        approveVM.postalCode = currentAddress.address.postalCode;
        approveVM.latitude = currentAddress.address.latitude;
        approveVM.longitude = currentAddress.address.longitude;
        approveVM.timeZoneId = currentAddress.address.timeZoneId;
        approveVM.unloadRate = currentAddress.unloadRate;
        approveVM.country = currentAddress.address.country;
        approveVM.phoneNumber = currentAddress.phoneNumber || null;
        approveVM.contactEmailBusiness = currentAddress.contactEmailBusiness;
        approveVM.contactNameBusiness = currentAddress.contactNameBusiness;
        approveVM.contactPhoneNumberBusiness = currentAddress.contactPhoneNumberBusiness;
        approveVM.contactEmailSite = currentAddress.contactEmailSite;
        approveVM.contactNameSite = currentAddress.contactNameSite;
        approveVM.contactPhoneNumberSite = currentAddress.contactPhoneNumberSite;
        approveVM.note = currentAddress.note;
        approveVM.isJobSite = currentAddress.isJobSite;
        approveVM.deliveryTypes = currentAddress.deliveryTypes;
        approveVM.isReviewed = currentAddress.isReviewed;

        currentAddress.deliveryHours.forEach((itm: DeliveryHourView) => {
            const hour = new DeliveryHourUpdate();
            hour.weekDays = itm.weekDays;
            hour.startTime = itm.startTime;
            hour.endTime = itm.endTime;

            approveVM.deliveryHours.push(hour);
        });

        setPageErrorMsg('');
        if (!validateAddressData(approveVM, true)) {
            return;
        }

        let response = await service.approveAddress(currentAddress.id, approveVM);
        if (response.status !== StatusCode.Success) {
            return;
        }

        let data = response?.data;
        if (data?.status === AddressApproveStatus.Success) {
            navigateToPage(Routes.APPROVALS_ADDRESSES);
            return;
        }

        if (data.status === AddressApproveStatus.NotApprovedGoogleHasSuggestion) {
            const useGoogleAddress = await showSuggestedAddressConfirmation(approveVM, data.suggestedAddress);
            if (useGoogleAddress) {
                approveVM.addressLine1 = data.suggestedAddress.addressLine1;
                approveVM.addressLine2 = data.suggestedAddress.addressLine2;
                approveVM.city = data.suggestedAddress.city;
                approveVM.stateProvince = data.suggestedAddress.stateProvince;
                approveVM.postalCode = data.suggestedAddress.postalCode;
                approveVM.country = data.suggestedAddress.country || '';
                approveVM.latitude = data.suggestedAddress.latitude;
                approveVM.longitude = data.suggestedAddress.longitude;
                approveVM.timeZoneId = data.suggestedAddress.timeZoneId;
                approveVM.force = true;
            }
            else if (useGoogleAddress === false) {
                approveVM.force = true;
            }
            else {
                // User cancelled the confirmation
                return;
            }
        }
        else if (data.status === AddressApproveStatus.NotApprovedGoogleNotFound) {
            const result = await showAddressConfirmation(approveVM);
            if (!result) {
                return;
            }
            approveVM.force = true;
        }
        else {
            setPageErrorMsg('Error occurred while approving the address. Please try again.');
            return;
        }

        response = await service.approveAddress(currentAddress.id, approveVM);
        if (response.status === StatusCode.Success && response.data.status === AddressApproveStatus.Success) {
            navigateToPage(Routes.APPROVALS_ADDRESSES);
            return;
        }
    };

    /**
     * Prompt the user to confirm whether to use the provided address even if google could not find a matching address.
     * @param address The original entered address
     */
    const showAddressConfirmation = async (addressVM: ShipToAddressApprove) => {
        const address = new ApproveAddress();
        address.addressLine1 = addressVM.addressLine1;
        address.addressLine2 = addressVM.addressLine2;
        address.city = addressVM.city;
        address.stateProvince = addressVM.stateProvince;
        address.postalCode = addressVM.postalCode;
        address.country = addressVM.country;
        address.latitude = addressVM.latitude;
        address.longitude = addressVM.longitude;
        address.timeZoneId = addressVM.timeZoneId;

        const message = (
            <>
                <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}>
                    Google could not find a matching address. Do you want to continue using the address you entered?
                </div>

                <div style={{ textAlign: 'left' }}>
                    <strong>Address:</strong>
                    {formatAddress(address)}
                </div>
            </>
        );

        const result = await ResultDialog.show({
            title: 'Address Confirmation',
            messageContent: message,
            affirmativeLabel: 'Yes',
            negativeLabel: 'Cancel'
        });

        return result;
    };

    const formatAddress = (addr: ApproveAddress) => (
        <div>
            <div>{addr.addressLine1}</div>
            {addr.addressLine2 && <div>{addr.addressLine2}</div>}
            <div>
                {addr.city}{addr.city && addr.stateProvince ? ', ' : ''}{addr.stateProvince} {addr.postalCode}
            </div>
            <div>{addr.country}</div>
            <div>Latitude: {addr.latitude}</div>
            <div>Longitude: {addr.longitude}</div>
            <div>TimeZoneId: {addr.timeZoneId}</div>
        </div>
    );

    const onCancel = () => {
        navigateToPage(Routes.APPROVALS_ADDRESSES);
    };

    const onBack = () => {
        navigateToPage(Routes.APPROVALS_ADDRESSES);
    };

    /**
     * Validate the current address to determine if it can
     * be saved or approved
     * @param addressVM The address model to validate
     * @param isApproving Whether the address is being approved
     */
    const validateAddressData = (addressVM: any, isApproving: boolean = false) => {
        const defaultError = ['This field is required'];
        const msgs = ModelValidator.validate(addressVM);

        if (!addressVM.addressLine1?.trim()) {
            msgs.addressLine1 = defaultError;
        }
        if (!addressVM.city?.trim()) {
            msgs.city = defaultError;
        }
        if (!addressVM.stateProvince?.trim()) {
            msgs.stateProvince = defaultError;
        }
        if (!addressVM.postalCode?.trim()) {
            msgs.postalCode = defaultError;
        }

        if (!Number.isFinite(addressVM.latitude)) {
            msgs.latitude = ['A valid latitude is required.'];
        }
        if (!Number.isFinite(addressVM.longitude)) {
            msgs.longitude = ['A valid longitude is required.'];
        }
        if (addressVM.latitude === 0 && addressVM.longitude === 0) {
            msgs.latitude = ['A valid latitude is required.'];
            msgs.longitude = ['A valid longitude is required.'];
        }

        if (isApproving && addressVM.isJobSite &&
            (!addressVM.contactNameBusiness?.trim() || !addressVM.contactEmailBusiness?.trim() || !addressVM.contactPhoneNumberBusiness?.trim() ||
                !addressVM.contactNameSite?.trim() || !addressVM.contactEmailSite?.trim() || !addressVM.contactPhoneNumberSite?.trim())) {
            msgs.jobSiteData = true;
        }

        if (!AppUtils.IsEmpty(msgs)) {
            setErrorMsgs(msgs);
            setPageErrorMsg('One or more required fields are missing. Please fill in all required fields and try again.');
            return false;
        }

        setErrorMsgs({});
        return true;
    };

    const updateHours = (controlId: string, groupsForControl: DeliveryHourView[], selectedDays: number, forceClear: boolean = false) => {

        const tmpAddress = { ...currentAddress };
        const toBeAdded: DeliveryHourView[] = [];
        let tmpHoursByControl: any = hoursByControl;

        if (forceClear) {
            tmpHoursByControl = {};
        }

        if (!tmpHoursByControl.hasOwnProperty(controlId)) {
            tmpHoursByControl[controlId] = [];
        }

        tmpHoursByControl[controlId] = groupsForControl;

        Object.keys(tmpHoursByControl).forEach((blahKey: string) => {

            const entry: DeliveryHourView[] = tmpHoursByControl[blahKey];
            entry.forEach((itm: DeliveryHourView) => {

                let hasMatch = false;

                for (let i = 0; i < toBeAdded.length; i++) {
                    const existing = toBeAdded[i];
                    if (itm.startTime === existing.startTime &&
                        (itm.endTime === existing.endTime) &&
                        (itm.weekDays === existing.weekDays)) {
                        hasMatch = true;
                        break;
                    }
                }

                if (!hasMatch) {
                    const newItem = new DeliveryHourView();
                    newItem.endTime = itm.endTime;
                    newItem.startTime = itm.startTime;
                    newItem.weekDays = itm.weekDays;
                    toBeAdded.push(newItem);
                }
            });
        });

        tmpAddress.deliveryHours = toBeAdded;

        setAddress(tmpAddress);

        let tmpGroupedHours = [...groupedHours];
        if (forceClear) {
            tmpGroupedHours = [{
                id: controlId,
                items: [],
                days: 1
            }];
        }

        tmpGroupedHours.forEach((itm: any) => {
            if (itm.id === controlId) {
                itm.items = groupsForControl;

                if (groupsForControl && groupsForControl.length === 0) {
                    itm.days = 0;
                }
                else {
                    itm.days = groupsForControl[0].weekDays;
                }
            }
        });

        setGroupedHours(tmpGroupedHours);
    };

    const removeHours = (idx: string) => {

        const tmpHours = [...groupedHours];
        const updated = tmpHours.reduce((acc: any[], curr: any) => {
            if (curr.id !== idx) {
                acc.push(curr);
            }
            return acc;
        }, []);

        // Update the grouped hours
        let adds: DeliveryHourView[] = [];
        updated.forEach((itm: any) => {
            adds = adds.concat(itm.items);
        });

        setGroupedHours(updated);

        // Update the address
        const tmpAddress = currentAddress;
        tmpAddress.deliveryHours = adds;
        setAddress(tmpAddress);

        // Update the hours by control
        const tmpControlHours: any = hoursByControl;
        delete tmpControlHours[idx];
        setHoursByControl(tmpControlHours);

        // Update the grouped hours display
        const all = AppUtils.groupHoursByDayOfWeek(currentAddress.deliveryHours);
        setDisplayHours(all);

    };

    const updateDisplayHours = () => {
        const all = AppUtils.groupHoursByDayOfWeek(currentAddress.deliveryHours);
        setDisplayHours(all);
    };

    const onCountryChange = (selectedCountry: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                country: (selectedCountry) ? (selectedCountry.id ? selectedCountry.id : selectedCountry) : null
            }
        };

        setAddress(tmpAddress);
    };

    const makeDefault = () => {
        const cId = uuid();
        setGroupedHours([{
            id: cId,
            items: [],
            days: 1
        }]);

        setHoursByControl({});

        const newView = new DeliveryHourView();
        newView.startTime = "08:00:00";
        newView.endTime = "17:00:00";
        newView.uniqueID = uuid();
        newView.weekDays = 62;

        updateHours(cId, [newView], 62, true);
    };

    const addHours = () => {
        const tmpHours = [...groupedHours];
        tmpHours.push({
            id: uuid(),
            items: [],
            days: 1
        });

        setGroupedHours(tmpHours);
    };

    const onTimeZoneChange = (selectedTimezone: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                timeZoneId: (selectedTimezone) ? selectedTimezone.id : null
            }
        };

        setAddress(tmpAddress);
    };

    const onPhoneNumberChanged = (e: any) => {
        const tmpAddress = {
            ...currentAddress,
            phoneNumber: e.target.value.replace(/\D/g, '')
        };

        setAddress(tmpAddress);
    };

    const onNameChanged = (evt: any) => {
        const tmpAddress = {
            ...currentAddress,
            name: evt.target.value
        };

        setAddress(tmpAddress);
    };

    const onAddressLine1Changed = (evt: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                addressLine1: evt.target.value
            }
        };

        setAddress(tmpAddress);
    };

    const onAddressLine2Changed = (evt: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                addressLine2: evt.target.value
            }
        };

        setAddress(tmpAddress);
    };

    const onCityChanged = (evt: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                city: evt.target.value
            }
        };

        setAddress(tmpAddress);
    };

    const onStateChanged = (evt: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                stateProvince: evt.target.value
            }
        };

        setAddress(tmpAddress);
    };

    const onPostalCodeChanged = (evt: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                postalCode: evt.target.value
            }
        };

        setAddress(tmpAddress);
    };

    const onLatitudeChange = (e: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                latitude: Number.parseFloat(e.target.value)
            }
        };

        setAddress(tmpAddress);
    };

    const onLongitudeChange = (e: any) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                longitude: Number.parseFloat(e.target.value)
            }
        };

        setAddress(tmpAddress);
    };

    const onContactPhoneNumberBusinessChanged = (e: any) => {
        const tmpAddress = {
            ...currentAddress,
            contactPhoneNumberBusiness: e.target.value.replace(/\D/g, '')
        };

        setAddress(tmpAddress);
    };

    const onContactPhoneNumberSiteChanged = (e: any) => {
        const tmpAddress = {
            ...currentAddress,
            contactPhoneNumberSite: e.target.value.replace(/\D/g, '')
        };

        setAddress(tmpAddress);
    };

    const onMapClick = (newLatitude: number, newLongitude: number) => {
        const tmpAddress = {
            ...currentAddress,
            address: {
                ...currentAddress.address,
                latitude: newLatitude,
                longitude: newLongitude
            }
        };

        setAddress(tmpAddress);
    };

    const selectNewDeliveryType = (key: string) => {
        var numVal = Number(key);
        var typeVal = numVal as AddressDeliveryType;
        if (typeVal) {
            setNewDeliveryType(typeVal);
        }
        else {
            setNewDeliveryType(undefined);
        }
    };

    const addDeliveryType = () => {
        const tmpAddress = { ...currentAddress };
        if (newDeliveryType) {
            tmpAddress.deliveryTypes.push(newDeliveryType);
            setAddress(tmpAddress);
        }

        var deliveryTypes = getRemainingDeliveryTypes(tmpAddress);
        setDeliveryTypes(deliveryTypes);
        setNewDeliveryType(undefined);
    };

    const removeDeliveryType = (idx: number) => {
        const tmpAddress = { ...currentAddress };
        tmpAddress.deliveryTypes.splice(idx, 1);
        setAddress(tmpAddress);

        var deliveryTypes = getRemainingDeliveryTypes(tmpAddress);
        setDeliveryTypes(deliveryTypes);
    }

    const getReviewDataDisplay = () => {
        if (!currentAddress?.reviewedOn || !currentAddress?.reviewUser) {
            return null;
        }
        const reviewDate = new Date(currentAddress.reviewedOn);
        const reviewDateDisplay = reviewDate.toLocaleString();
        return (
            <div className="approval-data" style={{ marginBottom: 12 }}>
                <strong>Reviewed by:</strong> {currentAddress.reviewUser} on {reviewDateDisplay}
            </div>
        );
    };

    return (
        <PageContent pageName={PageNames.TRANSPORTATION_ADDRESSES}>
            <PageHeading
                title={`Approve Address`}
                showBackBtn={true}
                onBackClick={onBack} >
                <div className="page-action-buttons">
                    {/*                     <button id="form-save" className="btn btn-wide new" onClick={() => onToggleReviewed(!currentAddress?.reviewedOn)}>
                        {!currentAddress?.reviewedOn ? 'Mark Reviewed' : 'Unmark Reviewed'}
                    </button> */}
                    <button id="form-save" className="btn new" onClick={onSave}>
                        Save
                    </button>
                    <button id="form-cancel" className="btn new negative" onClick={onCancel}>
                        Cancel
                    </button>
                    <button id="form-approve" className="btn new" onClick={onApprove}>
                        Approve
                    </button>
                </div>
            </PageHeading>
            <PageBody classes='edit-delivery-address'>
                {pageErrorMsg ? <PageLevelError message={pageErrorMsg} /> : null}

                {getReviewDataDisplay()}

                {currentAddress ? (
                    <>

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="dealerId"
                                    label="Dealer"
                                    disable={true}
                                    onChange={() => { return; }}
                                    value={`${currentAddress.customerId} ${currentAddress.customerName}`} />
                            </div>
                        </div>

                        <DataGroupHeader title="Location" />

                        <div className="row">
                            <div className="col-md-12">
                                <AddressEntryDisplay
                                    name={currentAddress.name}
                                    addressLine1={currentAddress.address.addressLine1}
                                    addressLine2={currentAddress.address.addressLine2}
                                    city={currentAddress.address.city}
                                    stateProvince={currentAddress.address.stateProvince}
                                    postalCode={currentAddress.address.postalCode}
                                    phoneNumber={currentAddress.phoneNumber}
                                    longitude={currentAddress.address.longitude}
                                    latitude={currentAddress.address.latitude}
                                    onClick={onMapClick}
                                    phoneNumberChanged={onPhoneNumberChanged}
                                    nameChanged={onNameChanged}
                                    addressLine1Changed={onAddressLine1Changed}
                                    addressLine2Changed={onAddressLine2Changed}
                                    cityChanged={onCityChanged}
                                    stateChanged={onStateChanged}
                                    postalCodeChanged={onPostalCodeChanged}
                                    errorMsgs={errorMsgs}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    type="number"
                                    id="latitude"
                                    label="Latitude"
                                    error={errorMsgs.latitude}
                                    value={currentAddress.address.latitude}
                                    onChange={onLatitudeChange}
                                />
                            </div>

                            <div className="col-md-6">
                                <TimeZoneInput
                                    id="address-timezone"
                                    label="Timezone"
                                    error={errorMsgs.timeZoneId}
                                    value={currentAddress.address.timeZoneId}
                                    onChange={onTimeZoneChange}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    type="number"
                                    id="longitude"
                                    label="Longitude"
                                    error={errorMsgs.longitude}
                                    value={currentAddress.address.longitude}
                                    onChange={onLongitudeChange}
                                />
                            </div>

                            <div className="col-md-6">
                                <CountryInput
                                    id="address-country"
                                    label="Country"
                                    error={errorMsgs.country}
                                    value={currentAddress.address.country}
                                    onChange={onCountryChange}
                                />
                            </div>
                        </div>

                        <DataGroupHeader title="Pending Orders" />

                        <div className="row">
                            <div className="col-md-12">

                                <Table tableClass="pending-orders-table"
                                    header={['Order Id', 'Description']}
                                    noResultsMessage="No pendingorders found that use this address.">
                                    {
                                        currentAddress.orders.map((itm: PendingAddressOrder, idx: number) => {
                                            return (
                                                <tr key={idx}>
                                                    <td>
                                                        {itm.id}
                                                        {itm.isFedex && <span className="badge badge-secondary ml-2">Fedex</span>}
                                                    </td>
                                                    <td>{itm.description}</td>
                                                </tr>
                                            );
                                        })
                                    }
                                </Table>
                            </div>
                        </div>

                        <DataGroupHeader title="Details" />

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    type="number"
                                    id="unloadRate"
                                    label="Unload Rate (units/hr)"
                                    error={errorMsgs.unloadRate}
                                    value={currentAddress.unloadRate}
                                    disable={false}
                                    onChange={(e: any) => setAddress({ ...currentAddress, unloadRate: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="label undefined" htmlFor="approvedDeliveryTypes">Approved Delivery Types</label>
                                    <table className="multi-select-table">
                                        <tbody>
                                            {
                                                currentAddress.deliveryTypes.map((dt, index) => (
                                                    <tr key={index}>
                                                        <td>{AddressDeliveryTypeMetadata[dt].display}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn-icon remove-btn"
                                                                onClick={() => removeDeliveryType(index)}>
                                                                <i className="la la-times" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            <tr>
                                                <td>
                                                    <select
                                                        id="newDeliveryType"
                                                        className="form-control"
                                                        value={newDeliveryType}
                                                        onChange={(e) => selectNewDeliveryType(e.target.value)}>
                                                        <option value="">Select a delivery type</option>
                                                        {deliveryTypes.map((dt) => (
                                                            <option key={dt.id} value={dt.id}>
                                                                {dt.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <button
                                                        id="add-delivery-type"
                                                        onClick={addDeliveryType}
                                                        type="button"
                                                        className="btn-icon add-btn" >
                                                        Add
                                                        <i className="la la-plus" />
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {
                            errorMsgs.jobSiteData
                                ? <div className="row">
                                    <div className="col">
                                        <span key="itemserror" className='page-action-error fixed-mode'>
                                            Contact name, email, and phone number for both the business and site are required for job sites.
                                        </span>
                                    </div>
                                </div>
                                : null
                        }

                        <div className="row" style={{ marginTop: 12 }}>
                            <div className="col-md-6">
                                <ToggleInput
                                    id="is-job-site"
                                    name="is-job-site"
                                    label="Is Job Site"
                                    checked={currentAddress.isJobSite}
                                    onClick={() => setAddress({ ...currentAddress, isJobSite: !currentAddress.isJobSite })}
                                />
                            </div>
                            <div className="col-md-6">
                                <ToggleInput
                                    id="is-reviewed"
                                    name="is-reviewed"
                                    label="Reviewed"
                                    checked={currentAddress.isReviewed}
                                    onClick={() => setAddress({ ...currentAddress, isReviewed: !currentAddress.isReviewed })}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="contact-name-business"
                                    label={currentAddress.isJobSite ? 'Contact Name(Business)*' : 'Contact Name(Business)'}
                                    value={currentAddress.contactNameBusiness}
                                    onChange={(e: any) => setAddress({ ...currentAddress, contactNameBusiness: e.target.value })}
                                />
                            </div>
                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="contact-name-site"
                                    label={currentAddress.isJobSite ? 'Contact Name(Site)*' : 'Contact Name(Site)'}
                                    value={currentAddress.contactNameSite}
                                    onChange={(e: any) => setAddress({ ...currentAddress, contactNameSite: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="row">

                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="contact-email-business"
                                    label={currentAddress.isJobSite ? 'Contact Email(Business)*' : 'Contact Email(Business)'}
                                    value={currentAddress.contactEmailBusiness}
                                    onChange={(e: any) => setAddress({ ...currentAddress, contactEmailBusiness: e.target.value })}
                                />
                            </div>
                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="contact-email-site"
                                    label={currentAddress.isJobSite ? 'Contact Email(Site)*' : 'Contact Email(Site)'}
                                    value={currentAddress.contactEmailSite}
                                    onChange={(e: any) => setAddress({ ...currentAddress, contactEmailSite: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="contact-phone-business"
                                    label={currentAddress.isJobSite ? 'Contact Phone Number(Business)*' : 'Contact Phone Number(Business)'}
                                    value={`tel: ${AppUtils.formatPhone(currentAddress.contactPhoneNumberBusiness ?? '')}`}
                                    onChange={onContactPhoneNumberBusinessChanged}
                                />
                            </div>
                            <div className="col-md-6">
                                <TextInput
                                    type="text"
                                    id="contact-phone-site"
                                    label={currentAddress.isJobSite ? 'Contact Phone Number(Site)*' : 'Contact Phone Number(Site)'}
                                    value={`tel: ${AppUtils.formatPhone(currentAddress.contactPhoneNumberSite ?? '')}`}
                                    onChange={onContactPhoneNumberSiteChanged}
                                />
                            </div>
                        </div>

                        <div className="row" style={{ marginTop: 12 }}>
                            <div className="col-md-12">
                                <TextArea
                                    id="address-note"
                                    label="Note"
                                    value={currentAddress.note}
                                    onChange={(e: any) => setAddress({ ...currentAddress, note: e.target.value })}
                                    defaultLines={4}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <PageActionHeading
                                    id="addlineitem"
                                    heading={
                                        <div className="hour-subheading">
                                            <span>Hours</span>
                                            {
                                                !fixedRangeMode
                                                    ? <button
                                                        className="btn-clear"
                                                        onClick={makeDefault}
                                                    >
                                                        (Set to Default)
                                                    </button>
                                                    : null
                                            }
                                        </div>
                                    }
                                    actionText="Add"
                                    actionIcon="la-plus-circle"
                                    actionDisable={false}
                                    actionClick={addHours} />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-check fixed-range-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={fixedRangeMode}
                                        disabled={false}
                                        onChange={() => setFixedRangeMode(!fixedRangeMode)}
                                        id="fixed-hours" />
                                    <label className="form-check-label" htmlFor="fixed-hours">
                                        Fixed Range Mode?
                                    </label>
                                </div>
                            </div>
                        </div>

                        {
                            errorMsgs.deliveryHours
                                ? <div className="row">
                                    <div className="col">
                                        <span key="itemserror" className='page-action-error fixed-mode'>
                                            You must provide at least one set of hours
                                        </span>
                                    </div>
                                </div>
                                : null
                        }

                        {
                            groupedHours && groupedHours.length > 0
                                ? groupedHours.map((itm: IGroupedHours, idx: number) => (
                                    <EditAddressHoursControl
                                        key={itm.id}
                                        id={itm.id}
                                        deliveryHours={itm.items}
                                        deliveryDays={itm.days}
                                        disabled={false}
                                        onUpdate={updateHours}
                                        onRemove={removeHours}
                                        showFixedRange={fixedRangeMode}
                                        onDisplayUpdate={updateDisplayHours}
                                    />
                                ))
                                : <div className="empty-placeholder-div">
                                    <h2>Click the add button to get started</h2>
                                </div>
                        }

                        {
                            groupedHours && groupedHours.length > 0
                                ? <React.Fragment>
                                    <div className="row">
                                        <div className="col">
                                            <PageActionHeading
                                                id="hours-display"
                                                heading="Hour Summary" />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col">

                                            <div className="display-hours-row">
                                                {
                                                    displayHours && Object.keys(displayHours).length > 0
                                                        ? Object.keys(displayHours).map((item: string, outerIdx: number) =>

                                                            displayHours[item].length > 0
                                                                ? <div className="display-hour" key={`outer-group-${outerIdx}`}>
                                                                    <p className="day-label">
                                                                        {WeekDaysMetadata[Number.parseInt(item, 10) as WeekDays].display}
                                                                    </p>
                                                                    {
                                                                        displayHours[item].map((itm: any, innerIdx: number) =>
                                                                            <p
                                                                                className="inner-hour"
                                                                                key={`inner-hour-${innerIdx}`}>
                                                                                {
                                                                                    fixedRangeMode
                                                                                        ? itm.start
                                                                                        : `${itm.start} to ${itm.end}`
                                                                                }
                                                                            </p>
                                                                        )
                                                                    }
                                                                </div>
                                                                : null
                                                        )
                                                        : <div className="empty-placeholder-div">
                                                            <h2>Please set some hours to review the summary.</h2>
                                                        </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                                : null
                        }
                    </>
                ) : null}
            </PageBody>
        </PageContent>
    );
};

export default AddressApprovalScene;
